import { Icon } from '@iconify/react';
import { Heart } from 'lucide-react';
import { useProjectInteractions } from '../hooks/useProjectInteractions';

interface ProjectCardProps {
  project: any;
  onClick: () => void;
  isLocked?: boolean;
  viewCount?: number;
  showStats?: boolean;
}

export function ProjectCard({
  project,
  onClick,
  isLocked = false,
  viewCount = 0,
  showStats = true,
}: ProjectCardProps) {
  const { likes, isLiked, isBookmarked, toggleLike, toggleBookmark } =
    useProjectInteractions(project.id, project.data?.category);

  const title = project.data?.title || 'Untitled Project';
  const architect = project.data?.architect || project.data?.author;
  const category = project.data?.category;
  const coverImage =
    project.data?.coverImage ||
    (typeof project.data?.images?.[0] === 'string'
      ? project.data.images[0]
      : project.data?.images?.[0]?.url) ||
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop';

  return (
    <div
      className={`rounded-2xl overflow-hidden border border-border bg-card group cursor-pointer
        active:scale-[0.98] transition-transform duration-150
        hover:shadow-md hover:border-orange-300
        ${isLocked ? 'opacity-60' : ''}`}
      onClick={isLocked ? undefined : onClick}
    >
      {/* ── Image ── */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={coverImage}
          alt={title}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
            isLocked ? 'blur-sm' : ''
          }`}
        />

        {isLocked ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 gap-1">
            <Icon icon="hugeicons:lock" className="h-6 w-6 text-white" />
            <p className="text-white text-xs font-medium">Upgrade to view</p>
          </div>
        ) : (
          <>
            {/* Like + Bookmark — larger tap targets on mobile */}
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button
                aria-label={isLiked ? 'Unlike' : 'Like'}
                onClick={(e) => { e.stopPropagation(); toggleLike(); }}
                className={`w-8 h-8 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors
                  ${isLiked ? 'bg-red-500 text-white' : 'bg-black/30 text-white hover:bg-black/50'}`}
              >
                <Heart className={`h-3.5 w-3.5 ${isLiked ? 'fill-current' : ''}`} />
              </button>
              <button
                aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
                onClick={(e) => { e.stopPropagation(); toggleBookmark(project.data?.category); }}
                className={`w-8 h-8 flex items-center justify-center rounded-full backdrop-blur-sm transition-colors
                  ${isBookmarked ? 'bg-orange-500 text-white' : 'bg-black/30 text-white hover:bg-black/50'}`}
              >
                <Icon icon="hugeicons:bookmark-01" className={`h-3.5 w-3.5 ${isBookmarked ? 'fill-current' : ''}`} />
              </button>
            </div>

            {/* Category badge */}
            {category && (
              <div className="absolute bottom-2 left-2">
                <span className="px-2 py-0.5 bg-black/60 text-white text-[10px] rounded-full backdrop-blur-sm capitalize">
                  {category}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Info ── */}
      <div className="p-2.5 md:p-3">
        <p className="text-xs font-medium text-foreground line-clamp-1 leading-snug mb-1">
          {title}
        </p>

        {showStats && (
          <div className="hidden md:flex items-center justify-between text-[10px] text-muted-foreground">
            {/* Likes + views */}
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-0.5">
                <Heart className="h-2.5 w-2.5" />
                {likes}
              </span>
              {viewCount > 0 && (
                <span className="flex items-center gap-0.5">
                  <Icon icon="hugeicons:view" className="h-2.5 w-2.5" />
                  {viewCount}
                </span>
              )}
            </div>

            {/* Architect name */}
            {architect && (
              <span className="truncate max-w-[90px] text-right">{architect}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
